import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { License, Order, SystemModuleLicense } from "../../api/api-generated";
import {
  useFindAllSystemModuleLicensesByCompanyIdQuery,
  useUpdateSystemModuleLicenseMutation,
} from "../../api/api-hooks";
import { Item } from "../../utils/sharedTypes";
import SystemModulesButton from "../system-module-licenses/SystemModulesButton";
import LicensesOptionsBarForOrder from "./LicensesOptionsBarForOrder";
import LicensesTable from "./LicensesTable";

interface Props {
  dialogOpen: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  license?: License;
  order?: Order;
}

const LicensesDialog = ({ dialogOpen, setDialogOpen, order }: Props) => {
  const [updateModule, { isSuccess, reset }] =
    useUpdateSystemModuleLicenseMutation();
  const { data: systemModules } =
    useFindAllSystemModuleLicensesByCompanyIdQuery({
      id: order?.company.id ? order.company.id : -1,
    });
  const [systemModuleList, setSystemModuleList] = useState<
    SystemModuleLicense[]
  >([]);
  useEffect(() => {
    if (systemModules) setSystemModuleList(systemModules);
  }, [systemModules]);
  const [selectedLicenses, setSelectedLicenses] = useState<Item[]>([]);

  const handleCheckSystemModule = (idx: number) => {
    if (!order) return;
    const modulesCopy = [...systemModuleList];
    const associatedModuleCopy = { ...modulesCopy[idx] };
    if (!associatedModuleCopy.orderId) associatedModuleCopy.orderId = order.id;
    else associatedModuleCopy.orderId = null;
    modulesCopy[idx] = associatedModuleCopy;
    setSystemModuleList(modulesCopy);
    reset();
  };

  const handleSaveSystemModules = async () => {
    for await (const module of systemModuleList) {
      await updateModule({
        id: module.id,
        updateSystemModuleLicenseDto: module,
      });
    }
  };
  return (
    <Dialog
      fullWidth
      maxWidth="xl"
      open={dialogOpen}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle>Manage Licenses</DialogTitle>
      <DialogContent>
        <Grid container spacing={1}>
          <Grid item container xs={8}>
            <Typography variant="subtitle2" style={{ marginBottom: "1rem" }}>
              {order &&
                `Licenses for ${
                  order.purchaseOrderName
                    ? "Order " + order.purchaseOrderName
                    : "Unnamed Order"
                }`}
            </Typography>
            <LicensesTable
              setSelectedLicenses={setSelectedLicenses}
              extraFilters={{
                order: order,
                excludeOrder: false,
              }}
            />
            {order && (
              <LicensesOptionsBarForOrder
                selectedLicenses={selectedLicenses}
                setSelectedLicenses={setSelectedLicenses}
                order={order}
              />
            )}
          </Grid>
          <Grid item container xs direction="column" spacing={1}>
            <Grid item>
              <Typography variant="h6">System-Level Modules</Typography>
            </Grid>
            <Grid item>
              <Typography>Modules to Link to Order:</Typography>
            </Grid>
            <Grid item>
              <List>
                {!systemModuleList ||
                  (systemModuleList.length < 1 && <ListItem>None</ListItem>)}
                {systemModuleList?.map((module, idx) => (
                  <ListItem key={module.id}>
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        tabIndex={-1}
                        disableRipple
                        checked={module.orderId === order?.id}
                        onChange={() => handleCheckSystemModule(idx)}
                      />
                    </ListItemIcon>
                    <ListItemText>{module.module}</ListItemText>
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item container spacing={1} justifyContent="flex-end">
              <Grid item>
                <Button
                  disabled={isSuccess}
                  variant="outlined"
                  onClick={handleSaveSystemModules}
                >
                  Link Selected Modules to Order
                </Button>
              </Grid>
              <Grid item>
                <SystemModulesButton
                  company={order?.company}
                  buttonText="Manage Company Modules"
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setDialogOpen(false);
            setSelectedLicenses([]);
            reset();
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LicensesDialog;
